/**
 * CORE-F — projecting authoritative inputs into renderable cells
 *
 * Pure. Every constitutional rule this capability must obey is enforced
 * here rather than in JSX, so each one is testable without rendering a
 * component — and so a future change to the visual layer cannot quietly
 * reintroduce a defect the doctrine forbids.
 *
 * The rules, and where each lives
 * -------------------------------
 *   "Unknown is not zero."           → `dimensionFrom` returns `no_evidence`
 *                                      for an absent row. It has no `value`
 *                                      field to default.
 *   "Failure is not absence."        → a source in `error` yields
 *                                      `unavailable`, never `no_evidence`.
 *   "Sparse must look sparse."       → `evidenceStrengthFrom` is a separate
 *                                      axis from the score.
 *   "Old evidence remains evidence." → recency is banded and reported;
 *                                      no proficiency value is ever altered.
 *   "Two stores, not one."           → portable and scheduling proficiency
 *                                      are carried side by side and their
 *                                      disagreement is disclosed.
 *   "One of them is primary."        → the portable Passport record is the
 *                                      displayed proficiency coordinate and
 *                                      the only one ever banded. That is a
 *                                      deliberate preference, not a tie.
 *
 * CORE-F originates nothing
 * -------------------------
 * That claim is load-bearing, so it is worth being precise about the one
 * place real arithmetic happens. `aggregateJobStatsByCategory` rolls
 * per-catalog-job efficiency up to a category. It does so by reconstructing
 * the exact sums the database already computed from — a lossless
 * re-aggregation of sufficient statistics, not a model. There is no
 * smoothing, no clipping, no invented weighting, and no inferred value
 * where the source cannot support one. Everything else in this file
 * computes only BANDS: presentation groupings over values somebody else
 * owns.
 */

import {
  EVIDENCE_SPARSE_BELOW,
  EVIDENCE_STRONG_AT_OR_ABOVE,
  PROFICIENCY_AGREEMENT_TOLERANCE,
  RECENCY_AGING_WITHIN_DAYS,
  RECENCY_RECENT_WITHIN_DAYS,
  type DimensionState,
  type EvidenceStrength,
  type HeatmapCell,
  type HeatmapSourceStates,
  type ProficiencyBand,
  type ProficiencyDivergence,
  type RecencyBand,
  type SourceStatus,
  portableProvenanceClassOf,
} from './types';

// =============================================================================
// Raw inputs
// =============================================================================

/** `technician_proficiencies` — CORE-E's portable record. */
export interface ProficiencyRowLite {
  category_key: string;
  proficiency_score: number | null;
  confidence_score: number | null;
  sample_size: number | null;
  updated_at?: string | null;
  /** Core-100 R4: the class the single portable writer recorded (demonstrated / manager_asserted / …). */
  provenance_class?: string | null;
}

/** `technician_diagnostic_stats` — CORE-I. */
export interface DiagnosticRowLite {
  category_key: string | null;
  diagnostic_competency_score: number | null;
  total_diagnostics: number | null;
  last_updated_at?: string | null;
}

/** `technician_soft_proficiencies` — CORE-AI. */
export interface ReadinessRowLite {
  category_key: string;
  soft_score: number | null;
  training_completions?: number | null;
  last_training_at?: string | null;
  /**
   * CORE-E-GAP-006: when qualifying work in this category discharged the
   * readiness. Non-null means the opportunity was taken and the demonstrated
   * figure has already moved through the canonical authority — so continuing
   * to present the boost as OUTSTANDING readiness would count the same
   * training twice, once as a prompt and once as a result.
   *
   * NULL means still outstanding, which is not the same as failed.
   */
  validated_at?: string | null;
}

/**
 * A category's completed-work figures, already rolled up.
 *
 * Efficiency is a PERCENT of expected time (100 = at book time), converted
 * once from the ratio the database stores.
 */
export interface JobStatsByCategoryLite {
  category: string;
  avgEfficiencyPercent: number | null;
  jobsCompleted: number | null;
  /**
   * True when completed work in this category had to be excluded from the
   * efficiency figure because its rows lacked the basis to contribute.
   */
  efficiencyPartial: boolean;
  /** True when no row in the category could contribute to efficiency. */
  efficiencyUnavailable: boolean;
}

export interface ProjectHeatmapInput {
  categories: ReadonlyArray<string>;
  sources: HeatmapSourceStates;
  /** `technician_proficiencies` rows. */
  proficiencies: ReadonlyArray<ProficiencyRowLite>;
  /**
   * The current shop's `technicians.proficiencies` map, if one was read.
   * An empty object is a legitimate state and means "this shop has set no
   * scheduling values", NOT "there is no portable evidence".
   */
  scheduling?: Readonly<Record<string, number>> | null;
  diagnostics: ReadonlyArray<DiagnosticRowLite>;
  readiness: ReadonlyArray<ReadinessRowLite>;
  jobStats: ReadonlyArray<JobStatsByCategoryLite>;
  /** Injected so recency is deterministic under test. */
  nowIso: string;
  /** Optional shortenings for narrow cells. Display only. */
  shortNames?: Readonly<Record<string, string>>;
}

// =============================================================================
// State construction
// =============================================================================

function finite(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Turn "what the source said about this category" into a state.
 *
 * The signature is the guarantee. `value` arriving as `null` or `undefined`
 * cannot become a number: the `no_evidence` branch has no numeric field, so
 * there is nothing for a `|| 0` to attach to further down. A genuine `0`
 * from the owning capability arrives as `known` and stays distinguishable
 * from absence forever after.
 */
export function dimensionFrom(
  sourceStatus: SourceStatus,
  value: number | null | undefined,
): DimensionState {
  if (sourceStatus === 'loading') return { status: 'loading' };
  if (sourceStatus === 'error') {
    return { status: 'unavailable', reason: 'read_failed' };
  }
  if (sourceStatus === 'not_linked') {
    return { status: 'unavailable', reason: 'not_linked' };
  }
  // Core-100 R4 wave 3: a denied read returned zero rows; that is not absence.
  if (sourceStatus === 'not_permitted') {
    return { status: 'unavailable', reason: 'not_permitted' };
  }
  const n = finite(value);
  // Read succeeded and there is no row, or the row holds no value for this
  // dimension. That is an absence of evidence, not a measurement of zero.
  if (n === null) return { status: 'no_evidence' };
  return { status: 'known', value: n };
}

// =============================================================================
// Banding
// =============================================================================

/**
 * Band a proficiency score.
 *
 * `unknown` is returned for anything that is not a `known` value — and it
 * is a band in its own right, not the bottom of the scale. The original
 * implementation had no such band, so an unworked category shared a colour
 * and a label with genuinely weak performance.
 */
export function bandFor(state: DimensionState): ProficiencyBand {
  if (state.status !== 'known') return 'unknown';
  const score = state.value;
  if (score >= 80) return 'expert';
  if (score >= 60) return 'proficient';
  if (score >= 40) return 'capable';
  if (score >= 20) return 'developing';
  return 'novice';
}

/**
 * Classify how much work stands behind a claim.
 *
 * `'unknown'` when the sample count itself could not be established, which
 * is different from a known zero: "we cannot tell how much work is behind
 * this" and "no work is behind this" are different things to tell somebody
 * about their own record.
 */
export function evidenceStrengthFrom(
  sampleSize: DimensionState,
): EvidenceStrength {
  if (sampleSize.status !== 'known') return 'unknown';
  const n = sampleSize.value;
  if (n <= 0) return 'none';
  if (n < EVIDENCE_SPARSE_BELOW) return 'sparse';
  if (n >= EVIDENCE_STRONG_AT_OR_ABOVE) return 'strong';
  return 'adequate';
}

/**
 * Band the age of the most recent evidence.
 *
 * Reported only. No value is altered because evidence is old — CORE-E does
 * not provide a decayed figure, so inventing one here would be CORE-F
 * originating a capability claim, which it may not do.
 *
 * An absent or unparseable timestamp yields `'unknown'`, never `'older'`.
 */
export function recencyBandFor(
  isoDate: string | null | undefined,
  nowIso: string,
): RecencyBand {
  if (!isoDate) return 'unknown';
  const then = Date.parse(isoDate);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(then) || !Number.isFinite(now)) return 'unknown';
  const days = (now - then) / 86_400_000;
  // A timestamp in the future is not "recent", it is unexplained.
  if (days < 0) return 'unknown';
  if (days <= RECENCY_RECENT_WITHIN_DAYS) return 'recent';
  if (days <= RECENCY_AGING_WITHIN_DAYS) return 'aging';
  return 'older';
}

/**
 * How CORE-E's two stores relate for one category.
 *
 * Only `known` values can agree or disagree. A loading or failed read is
 * not a disagreement, and must not be reported as one.
 */
export function divergenceFor(
  portable: DimensionState,
  scheduling: DimensionState,
): ProficiencyDivergence {
  const p = portable.status === 'known';
  const s = scheduling.status === 'known';
  if (p && s) {
    const delta = Math.abs(
      (portable as { value: number }).value -
        (scheduling as { value: number }).value,
    );
    return delta <= PROFICIENCY_AGREEMENT_TOLERANCE ? 'agree' : 'differ';
  }
  if (p) return 'portable_only';
  if (s) return 'scheduling_only';
  return 'none';
}

// =============================================================================
// Projection
// =============================================================================

function indexBy<T>(
  rows: ReadonlyArray<T>,
  key: (row: T) => string | null | undefined,
): Map<string, T> {
  const out = new Map<string, T>();
  for (const row of rows ?? []) {
    if (!row) continue;
    const k = key(row);
    if (!k) continue;
    // First wins: a duplicate category row is a source defect, and silently
    // preferring the later one would make the render non-deterministic.
    if (!out.has(k)) out.set(k, row);
  }
  return out;
}

/**
 * Build one cell per category in the taxonomy.
 *
 * Every category in the taxonomy gets a cell, including ones with no
 * evidence at all — because "you have never worked this" is a fact worth
 * showing, and showing it as an explicit unknown is precisely how the
 * capability avoids showing it as a zero.
 *
 * Sources are handled independently. One failing dimension yields
 * `unavailable` for that dimension only; the dimensions that loaded still
 * render. Partial-but-honest beats all-or-nothing collapse.
 *
 * The two proficiency stores are read INDEPENDENTLY, per category. A shop
 * scheduling map that is empty, or that simply lacks this category,
 * contributes no claim here and cannot suppress the portable record. That
 * was the defect this pass exists to fix: `!!liveProficiencies` is true for
 * `{}`, and an empty object was erasing every portable row a technician had.
 */
export function projectHeatmap(input: ProjectHeatmapInput): HeatmapCell[] {
  const profByCat = indexBy(input.proficiencies, (r) => r.category_key);
  const diagByCat = indexBy(input.diagnostics, (r) => r.category_key);
  const readyByCat = indexBy(input.readiness, (r) => r.category_key);
  const statsByCat = indexBy(input.jobStats, (r) => r.category);
  const schedulingMap = input.scheduling ?? null;

  return (input.categories ?? []).map((category) => {
    const prof = profByCat.get(category);
    const diag = diagByCat.get(category);
    const ready = readyByCat.get(category);
    const stats = statsByCat.get(category);

    const portable = dimensionFrom(
      input.sources.proficiency,
      prof?.proficiency_score,
    );

    // Per category, and only where the map actually holds this key. An
    // absent key is "this shop has set nothing here", which is a state, not
    // a zero and not a reason to discard the portable record.
    const schedulingRaw =
      schedulingMap && Object.prototype.hasOwnProperty.call(schedulingMap, category)
        ? schedulingMap[category]
        : null;
    const scheduling = dimensionFrom(input.sources.scheduling, schedulingRaw);

    // The tile shows demonstrated capability where it exists, because that
    // is CORE-F's constitutional subject. Falling back to the scheduling
    // value is not a substitution: it travels with its own provenance and
    // its own explanation, and the reader is told which one they are seeing.
    // The primary coordinate is the portable record, unconditionally.
    //
    // There is deliberately no fallback. The capability bands are claims
    // about demonstrated capability, so a value whose provenance CORE-E
    // does not record must never be run through them: a scheduling-only 85
    // rendering "Expert" would be the system vouching for something it has
    // never seen. When the portable record has nothing, the honest tile is
    // its own state — no evidence, still loading, or could not be read —
    // and the shop's number is shown beside it as context instead.
    const proficiency = portable;

    // Confidence and sample size are read from the portable row exactly as
    // stored, whatever the scheduling map holds. They are not synthesised,
    // not defaulted, and not described downstream as derived. (Until Core-100
    // R2 CORE-E the DMC-F sync trigger wrote a constant `confidence_score =
    // 0.5`; it now writes the authority's computed confidence or NULL, and
    // the legacy constants were restated as unknown.)
    const confidence: DimensionState = dimensionFrom(
      input.sources.proficiency,
      prof?.confidence_score,
    );
    const sampleSize: DimensionState = dimensionFrom(
      input.sources.proficiency,
      // `sample_size` is nullable in the schema, and the sync trigger never
      // writes it. A null is "we don't know how much work", not "no work".
      prof?.sample_size,
    );

    const diagnostic = dimensionFrom(
      input.sources.diagnostic,
      diag?.diagnostic_competency_score,
    );
    // Validated readiness is no longer readiness. The training is not erased
    // — the row keeps it, and which job discharged it — but the dimension
    // stops claiming an outstanding opportunity that has already been taken.
    const readiness = dimensionFrom(
      input.sources.readiness,
      ready?.validated_at ? null : ready?.soft_score,
    );

    // Efficiency that could not be reconstructed from sufficient statistics
    // is reported as lacking a basis — never as zero, and never as a number
    // assembled from the rows that happened to be usable without saying so.
    const efficiency: DimensionState =
      input.sources.jobStats === 'ok' && stats?.efficiencyUnavailable
        ? { status: 'unavailable', reason: 'insufficient_basis' }
        : dimensionFrom(input.sources.jobStats, stats?.avgEfficiencyPercent);

    const volume = dimensionFrom(input.sources.jobStats, stats?.jobsCompleted);

    // Recency comes from the portable row's own timestamp — a value the
    // owning capability already writes. CORE-F derives no freshness of its
    // own. The scheduling map has no per-category timestamp at all, so it
    // contributes no recency; dating it from anything else would be
    // inventing a fact.
    const recencyDate =
      input.sources.proficiency === 'ok' ? (prof?.updated_at ?? null) : null;

    const cell: HeatmapCell = {
      category,
      displayName: input.shortNames?.[category] ?? category,
      proficiency,
      band: bandFor(proficiency),
      portable,
      portableClass:
        input.sources.proficiency === 'ok' && prof ? portableProvenanceClassOf(prof.provenance_class) : null,
      scheduling,
      divergence: divergenceFor(portable, scheduling),
      confidence,
      sampleSize,
      evidence: evidenceStrengthFrom(sampleSize),
      recency: recencyBandFor(recencyDate, input.nowIso),
      recencyDate,
      diagnostic,
      readiness,
      efficiency,
      efficiencyPartial:
        efficiency.status === 'known' && !!stats?.efficiencyPartial,
      volume,
      hasAnyEvidence:
        proficiency.status === 'known' ||
        scheduling.status === 'known' ||
        diagnostic.status === 'known' ||
        readiness.status === 'known' ||
        efficiency.status === 'known' ||
        volume.status === 'known',
    };

    return cell;
  });
}

// =============================================================================
// Completed-work roll-up
// =============================================================================

/** One `technician_job_learning_stats` row, as this module needs it. */
export interface JobLearningStatRowLite {
  job_category: string | null;
  avg_efficiency: number | null;
  avg_actual_minutes: number | null;
  jobs_completed: number | null;
}

/**
 * Roll per-catalog-job stat rows up to per-category figures.
 *
 * The arithmetic, and why it is what it is
 * ----------------------------------------
 * `technician_job_learning_stats` is keyed by catalog job, so a category
 * figure has to be re-aggregated across several rows. The obvious move —
 * averaging each row's `avg_efficiency`, weighted by `jobs_completed` — is
 * WRONG, and this repository already learned that lesson the expensive way:
 * migration `20260423130000_fix_learning_stats_avg_efficiency.sql` exists
 * solely because averaging efficiency ratios produces values that
 * misrepresent the technician. Efficiency is a ratio of totals, and ratios
 * of totals do not average by count.
 *
 * The canonical convention is `SUM(predicted) / SUM(actual)`, and both
 * writers of this table use it. Fortunately the row carries enough to
 * reconstruct those sums exactly. Both writers filter to
 * `actual_minutes IS NOT NULL AND actual_minutes > 0` and then compute
 * `COUNT(*)` and `AVG(actual_minutes)` over the SAME rows, so:
 *
 *     actual_total    = avg_actual_minutes × jobs_completed      (exact)
 *     predicted_total = avg_efficiency × actual_total            (exact)
 *
 * and the category figure is `Σ predicted_total / Σ actual_total`. That is
 * a lossless re-aggregation of sufficient statistics — the same number the
 * database would produce if it grouped by category directly — not a model
 * CORE-F invented.
 *
 * When a row cannot support that
 * ------------------------------
 * A row missing `avg_actual_minutes`, or carrying a non-positive one, or
 * missing `avg_efficiency`, cannot contribute its denominator without
 * guessing at it. Such rows contribute NOTHING to efficiency and are
 * counted, so the resulting figure can be labelled partial. If no row in a
 * category is usable, efficiency is unavailable rather than zero.
 *
 * Completed-work volume is independent: it comes from `jobs_completed`
 * alone, so it survives intact even when efficiency cannot be computed.
 *
 * Category vocabulary
 * -------------------
 * The writing trigger stores `COALESCE(proficiency_category, category)`, so
 * a row may arrive carrying a raw catalog key rather than a proficiency
 * category name. `normalise` maps what it can; what it cannot is COUNTED,
 * not dropped silently, because unattributable completed work means a
 * category's volume is a lower bound and the screen has to be able to say
 * so. Quietly discarding those rows would let real work disappear behind a
 * confident-looking "no data".
 */
export function aggregateJobStatsByCategory(
  rows: ReadonlyArray<JobLearningStatRowLite>,
  normalise: (raw: string) => string | null = (raw) => raw,
): { byCategory: JobStatsByCategoryLite[]; unattributedRows: number } {
  const acc = new Map<
    string,
    {
      predictedTotal: number;
      actualTotal: number;
      completed: number;
      usableRows: number;
      unusableRows: number;
    }
  >();
  let unattributedRows = 0;

  for (const row of rows ?? []) {
    if (!row) continue;
    const category = row.job_category ? normalise(row.job_category) : null;
    if (!category) {
      unattributedRows += 1;
      continue;
    }

    const entry = acc.get(category) ?? {
      predictedTotal: 0,
      actualTotal: 0,
      completed: 0,
      usableRows: 0,
      unusableRows: 0,
    };

    const completed = finite(row.jobs_completed) ?? 0;
    entry.completed += Math.max(0, completed);

    const efficiency = finite(row.avg_efficiency);
    const avgActual = finite(row.avg_actual_minutes);

    // Reconstruct this row's denominator, or decline to contribute. Every
    // condition here is one under which the row's real actual-minute total
    // is unknowable; using a substitute would silently fabricate part of
    // the number.
    const actualTotal =
      avgActual !== null && avgActual > 0 && completed > 0
        ? avgActual * completed
        : null;

    if (efficiency !== null && actualTotal !== null) {
      entry.predictedTotal += efficiency * actualTotal;
      entry.actualTotal += actualTotal;
      entry.usableRows += 1;
    } else {
      entry.unusableRows += 1;
    }

    acc.set(category, entry);
  }

  const byCategory = [...acc.entries()].map(([category, e]) => ({
    category,
    avgEfficiencyPercent:
      e.actualTotal > 0
        ? // ratio -> percent of expected, converted exactly once:
          // 1.0 (at book time) renders as 100%.
          Math.round((e.predictedTotal / e.actualTotal) * 1000) / 10
        : null,
    jobsCompleted: e.completed,
    efficiencyPartial: e.usableRows > 0 && e.unusableRows > 0,
    efficiencyUnavailable: e.usableRows === 0,
  }));

  return { byCategory, unattributedRows };
}
